import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { DESTINATIONS } from "../../game/destinations";
import { ResultScreen } from "./ResultScreen";
import { JOURNEY_STEP } from "./motionPresets";

const STEP_MS = 140;
const ARRIVAL_MS = 1300;

const JourneyStep = ({ name, index, finalStop }) => (
  <motion.li
    {...JOURNEY_STEP}
    data-testid={`journey-step-${index}`}
    className={`flex items-center gap-3 ${
      finalStop ? "font-display text-4xl py-3" : "text-[15px] font-medium text-[#14233D]/75"
    }`}
  >
    {finalStop ? (
      <>
        <span className="journey-dot" />
        <span className="journey-final">{name.toUpperCase()}</span>
      </>
    ) : (
      <>
        <span className="w-5 h-5 rounded-full bg-[#55D7F3]/20 flex items-center justify-center">
          <Check size={12} className="text-[#147E9D]" />
        </span>
        {name}
      </>
    )}
  </motion.li>
);

// Advances one destination every STEP_MS, then flags arrival after ARRIVAL_MS.
const useJourneyProgress = (reachedIndex) => {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const arrived = step >= reachedIndex;
    const t = setTimeout(
      () => (arrived ? setDone(true) : setStep((s) => s + 1)),
      arrived ? ARRIVAL_MS : STEP_MS,
    );
    return () => clearTimeout(t);
  }, [step, reachedIndex]);

  return { step, done };
};

export const Journey = ({ reachedIndex, assignments, challenge, boardCode, onBoardCreated, onReplay }) => {
  const { step, done } = useJourneyProgress(reachedIndex);
  const endRef = useRef(null);
  const final = DESTINATIONS[reachedIndex];
  const isLaughTale = reachedIndex === DESTINATIONS.length - 1;

  useEffect(() => {
    if (done) window.scrollTo({ top: 0 });
    else endRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [step, done]);

  if (done) {
    return <ResultScreen assignments={assignments} island={final.name} isLaughTale={isLaughTale} destinationIndex={reachedIndex} challenge={challenge} boardCode={boardCode} onBoardCreated={onBoardCreated} onReplay={onReplay} />;
  }

  return (
    <main className="max-w-md mx-auto px-5 pt-8 pb-16 min-h-[100dvh] relative z-10" data-testid="journey-screen">
      <p className="label-mono text-[#147E9D]">Il viaggio</p>
      <ol className="mt-5 space-y-1" data-testid="journey-list">
        {DESTINATIONS.slice(0, step + 1).map((d, i) => (
          <JourneyStep key={d.name} name={d.name} index={i} finalStop={i === step && step === reachedIndex} />
        ))}
      </ol>
      <div ref={endRef} />
    </main>
  );
};
